import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import Spinner from "../../spinner/Spinner";
import { useNavigate, useLocation } from "react-router-dom";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import formatDate from "../../../components/utils/DateFormatter";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import AxiosApiCall from "../../../api/AxiosApiCall";

const BranchInventoryRequestDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const requestId = location.state?.requestId;
    const fromHistory = location.state?.fromHistory || false;

    const [request, setRequest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);


    // Approval state per item
    const [approvedQtys, setApprovedQtys] = useState({});
    const [notes, setNotes] = useState("");
    const [rejectionReason, setRejectionReason] = useState("");

    const { permissions } = useContext(AuthContext);


      //axios api encryption call
  const axios = AxiosApiCall();

    const fetchRequestDetails = async () => {
        if (!requestId) return;
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) throw new Error("No access token found.");

            const response = await axios.get(ApiServices.getBranchInventoryRequestShow, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                params: { request_id: requestId },
                withCredentials: true,
            });

            const { status, data } = response.data;
            if (status === 200 && data) {
                setRequest(data);
                // Initialize approved quantities to 0
                const qtys = {};
                // data.items?.forEach((item) => {
                //     qtys[item.id] = item.quantity_approved || 0;
                // });
                data.items?.forEach((item) => {
                    qtys[item.id] = item.quantity_requested; // Set to requested quantity, not 0
                });
                setApprovedQtys(qtys);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequestDetails();
    }, [requestId]);

    const handleApprove = async () => {
        const items = request?.items?.map((item) => ({
            item_id: item.id,
            quantity_approved: parseInt(approvedQtys[item.id] || 0),
        }));

        const result = await Swal.fire({
            title: "Approve Request?",
            text: "This will deduct stock from head office inventory and charge the branch wallet.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f97316",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Approve",
        });

        if (!result.isConfirmed) return;

        setActionLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            const response = await axios.post(
                ApiServices.approveBranchInventoryRequest,
                { request_id: requestId, items, notes },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            const { status, message } = response.data;
            if (status === 200) {
                toast.success(message || "Request approved successfully!");
                navigate("/operations/branch/inventory-requests");
            } else {
                toast.error(message || "Approval failed.");
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.error("Please provide a rejection reason.");
            return;
        }

        const result = await Swal.fire({
            title: "Reject Request?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Reject",
        });

        if (!result.isConfirmed) return;

        setActionLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            const response = await axios.post(
                ApiServices.rejectBranchInventoryRequest,
                { request_id: requestId, rejection_reason: rejectionReason },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            const { status, message } = response.data;
            if (status === 200) {
                toast.success(message || "Request rejected successfully!");
                navigate("/operations/branch/inventory-requests");
            } else {
                toast.error(message || "Rejection failed.");
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case "pending": return "bg-orange-100 text-orange-700";
            case "approved": return "bg-green-100 text-green-700";
            case "partially_approved": return "bg-blue-100 text-blue-700";
            case "rejected": return "bg-red-100 text-red-700";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    if (loading) return <MainLayouts><Spinner loading={loading} /></MainLayouts>;

    if (!request) return (
        <MainLayouts>
            <div className="p-6 text-center text-gray-500">Request not found.</div>
        </MainLayouts>
    );

    const isPending = request.status === "pending";

    return (
        <MainLayouts>
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => navigate(fromHistory ? "/operations/branch/inventory-requests/history" : "/operations/branch/inventory-requests")}
                        className="text-orange1 hover:text-orange-600"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">Inventory Request Details</h1>
                        <p className="text-gray-500">{request.request_number}</p>
                    </div>
                    <span className={`ml-auto px-3 py-1 rounded-full text-sm font-semibold ${getStatusClass(request.status)}`}>
                        {request.status?.replace(/_/g, " ").toUpperCase()}
                    </span>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Branch</p>
                        <p className="font-bold text-gray-800">{request.branch?.name}</p>
                        <p className="text-sm text-gray-500">{request.branch?.email}</p>
                        <p className="text-sm text-gray-500">{request.branch?.phone_number}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Wallet: <span className="font-semibold text-green-600">{CurrencyFormatter(request.branch?.wallet_balance || 0)}</span>
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Head Office</p>
                        <p className="font-bold text-gray-800">{request.head_office?.name}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Timeline</p>
                        <p className="text-sm text-gray-600">Requested: <span className="font-medium">{formatDate(request.requested_at)}</span></p>
                        {request.reviewed_at && (
                            <p className="text-sm text-gray-600">Reviewed: <span className="font-medium">{formatDate(request.reviewed_at)}</span></p>
                        )}
                        <p className="text-sm text-gray-600 mt-1">Items: <span className="font-medium">{request.total_items}</span></p>
                    </div>
                </div>

                {/* Items Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b">
                        <h2 className="text-lg font-semibold">Requested Items</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Requested</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HO Available Stock</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                                    {isPending && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty to Approve</th>
                                    )}
                                    {!isPending && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty Approved</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {request.items?.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.product_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.product_category}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity_requested}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span className={item.inventory_available_stock <= 0 ? "text-red-600 font-semibold" : "text-green-600 font-semibold"}>
                                                {item.inventory_available_stock}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{CurrencyFormatter(item.unit_price || 0)}</td>
                                        {isPending ? (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={item.quantity_requested}
                                                        value={approvedQtys[item.id] || item.quantity_requested} // Fallback to requested if state not set
                                                        onChange={(e) => {
                                                            const newValue = parseInt(e.target.value) || 0;
                                                            // Ensure value doesn't exceed max
                                                            const validValue = Math.min(newValue, item.quantity_requested);
                                                            setApprovedQtys((prev) => ({ ...prev, [item.id]: validValue }));
                                                        }}
                                                        className="w-24 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange1"
                                                    />
                                                </td>
                                            </td>
                                        ) : (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.quantity_approved}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Rejection reason if rejected */}
                {request.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-red-700 font-semibold">Rejection Reason:</p>
                        <p className="text-sm text-red-600 mt-1">{request.rejection_reason}</p>
                    </div>
                )}

                {/* Notes if any */}
                {request.notes && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-blue-700 font-semibold">Notes:</p>
                        <p className="text-sm text-blue-600 mt-1">{request.notes}</p>
                    </div>
                )}

                {/* Action Panel (only for pending) */}
                {isPending && (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold mb-4">Actions</h2>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Approval Notes (optional)</label>
                            <textarea
                                rows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add notes for the branch..."
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange1"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                            <textarea
                                rows={2}
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Required if rejecting..."
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                            />
                        </div>

                        {permissions.some(permission => permission.name === 'branch.inventory.edit') && (
                            <div className="flex gap-4">
                                <button
                                    onClick={handleApprove}
                                    disabled={actionLoading}
                                    className="bg-orange1 text-white px-6 py-2 rounded hover:bg-orange-600 transition text-sm font-semibold disabled:opacity-50"
                                >
                                    {actionLoading ? "Processing..." : "Approve Request"}
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={actionLoading}
                                    className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition text-sm font-semibold disabled:opacity-50"
                                >
                                    {actionLoading ? "Processing..." : "Reject Request"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </MainLayouts>
    );
};

export default BranchInventoryRequestDetails;
